/**
 * BookingDetailModal — admin booking detail (Backlog E.4 · Q1).
 *
 * Renders when the admin clicks any event on the calendar. Shows id,
 * status, customer / provider names, address, total, lifecycle ISO
 * timestamps.
 *
 * Phase 3.3 G1: adds a single ACTION button — "Refund booking" — when
 * `payment_status === "paid"`. The button is hard-disabled (with the
 * dispatch-locked tooltip) while the admin is impersonating; the
 * backend ALSO returns 403 in that state (defense in depth).
 *
 * All other admin booking mutations still belong in their existing
 * Backlog D confirm-gated flows; we are NOT adding a second ungated
 * path through the calendar for non-refund actions.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { REFUND_DISABLED_WHILE_IMPERSONATING } from "@/lib/impersonationStrings";


function _formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
  }).format(cents / 100);
}

function _formatIsoLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_PILL_CLASS = {
  payment_pending: "bg-pp-surface-2 text-pp-text border border-pp-border",
  accepted: "bg-[#F89623] text-white",
  in_progress: "bg-[#F89623] text-white",
  completed: "bg-pp-surface-2 text-pp-text-muted",
  cancelled: "bg-[#94A3B8] text-white line-through",
};

function StatusPill({ status }) {
  const cls = STATUS_PILL_CLASS[status] || "bg-pp-surface-2 text-pp-text";
  const label = status ? status.replace(/_/g, " ") : "unknown";
  return (
    <span
      data-testid="admin-booking-detail-status-pill"
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function FieldRow({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-pp-text-muted">
        {label}
      </dt>
      <dd
        className="text-right text-sm text-pp-text"
        data-testid={testid}
      >
        {value}
      </dd>
    </div>
  );
}


// Phase 3.4 — DisputeBanner. Read-only chargeback indicator in the
// admin BookingDetailModal. 4 visual variants keyed on
// `booking.dispute_status`. NO action buttons at v1 (evidence
// submission deferred to Phase 3.4.x). Locked copy matches the
// dispatch §6.2 table.
//
//   open   / under_review   → red, all-caps DISPUTE OPEN (urgency)
//   lost                    → red, all-caps DISPUTE LOST (urgency)
//   won                     → green, sentence case (resolved positive)
//   warning_closed          → gray, sentence case (informational)
function _formatBannerDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "2-digit",
  });
}

function DisputeBanner({ booking }) {
  const status = booking?.dispute_status;
  if (!status) return null;
  const amountCents = booking?.dispute_amount_cents ?? 0;
  const amount = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
  }).format(amountCents / 100);
  const closedAt = _formatBannerDate(booking?.updated_at);

  // Map status → visual variant + copy.
  const isOpen = status === "open" || status === "under_review";
  const isLost = status === "lost";
  const isWon = status === "won";
  const isWarningClosed = status === "warning_closed";

  let containerCls = "border-pp-border bg-pp-surface-2 text-pp-text";
  let copy = null;

  if (isOpen) {
    containerCls = "border-red-300 bg-red-50 text-red-900";
    copy = (
      <>
        <span aria-hidden="true">⚠️</span>{" "}
        <strong>DISPUTE OPEN</strong> — {amount} in chargeback.
        Status: {status.replace(/_/g, " ")}.
      </>
    );
  } else if (isLost) {
    containerCls = "border-red-400 bg-red-100 text-red-900";
    copy = (
      <>
        <span aria-hidden="true">⚠️</span>{" "}
        <strong>DISPUTE LOST</strong> — {amount} in chargeback.
        {closedAt ? ` Closed ${closedAt}.` : ""}
      </>
    );
  } else if (isWon) {
    containerCls = "border-green-300 bg-green-50 text-green-900";
    copy = (
      <>Dispute resolved — won.{closedAt ? ` Closed ${closedAt}.` : ""}</>
    );
  } else if (isWarningClosed) {
    containerCls = "border-pp-border bg-pp-surface-2 text-pp-text-muted";
    copy = (
      <>
        Dispute warning closed — no funds withdrawn.
        {closedAt ? ` Closed ${closedAt}.` : ""}
      </>
    );
  } else {
    // Unknown status — render the raw status so admin sees something.
    containerCls = "border-pp-border bg-pp-surface-2 text-pp-text";
    copy = <>Dispute: {status.replace(/_/g, " ")}</>;
  }

  return (
    <div
      data-testid="admin-booking-detail-dispute-banner"
      data-dispute-status={status}
      className={`mb-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${containerCls}`}
      role="alert"
    >
      <div
        data-testid={`admin-booking-detail-dispute-banner-${status}`}
        className="flex-1"
      >
        {copy}
      </div>
      <span
        data-testid="admin-booking-detail-dispute-amount"
        className="sr-only"
      >
        {amount}
      </span>
    </div>
  );
}


export default function BookingDetailModal({ booking, onClose, user, onRefund }) {
  const open = !!booking;
  const b = booking || {};
  const address = b.service_address || {};
  const addressLines = [
    address.line1,
    address.line2,
    [address.city, address.state, address.zip].filter(Boolean).join(", "),
  ].filter(Boolean);

  // G1 — Refund affordance gate. Button is rendered only when there's
  // money to refund (`payment_status === "paid"`). When the admin is
  // impersonating, the button stays in the DOM but is disabled +
  // wrapped in a tooltip with the locked copy.
  const isImpersonating = !!user?.is_impersonating;
  const isPaid = b.payment_status === "paid";
  const isAlreadyRefunded =
    b.payment_status === "refunded" || b.payment_status === "partially_refunded";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-3xl sm:max-w-md"
        data-testid="admin-booking-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Booking detail</span>
            <StatusPill status={b.status} />
          </DialogTitle>
        </DialogHeader>

        {/* Phase 3.4 — DisputeBanner. Conditionally rendered when
            `b.dispute_status != null`. Read-only, no actions in v1. */}
        <DisputeBanner booking={b} />

        <div data-testid="admin-booking-detail-body" className="space-y-2">
          <FieldRow
            label="ID"
            value={
              <span className="font-mono text-xs break-all">
                {b.id || "—"}
              </span>
            }
            testid="admin-booking-detail-id"
          />
          <FieldRow
            label="Customer"
            value={b.customer_name || "—"}
            testid="admin-booking-detail-customer"
          />
          <FieldRow
            label="Provider"
            value={b.provider_business_name || "—"}
            testid="admin-booking-detail-provider"
          />
          <FieldRow
            label="Date"
            value={
              <>
                {b.service_date || "—"}
                {b.preferred_window?.start && (
                  <>
                    {" · "}
                    {b.preferred_window.start}–{b.preferred_window.end}
                  </>
                )}
              </>
            }
            testid="admin-booking-detail-date"
          />
          <FieldRow
            label="Address"
            value={
              addressLines.length ? (
                <div className="flex flex-col items-end">
                  {addressLines.map((line, i) => (
                    <span key={i}>{line}</span>
                  ))}
                </div>
              ) : (
                "—"
              )
            }
            testid="admin-booking-detail-address"
          />
          <FieldRow
            label="Total"
            value={_formatCents(b.total_amount_cents)}
            testid="admin-booking-detail-total"
          />

          <div className="mt-3 border-t border-pp-border pt-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-pp-text-muted">
              Lifecycle
            </div>
            <FieldRow
              label="Created"
              value={_formatIsoLocal(b.created_at)}
              testid="admin-booking-detail-created-at"
            />
            {b.accepted_at && (
              <FieldRow
                label="Accepted"
                value={_formatIsoLocal(b.accepted_at)}
                testid="admin-booking-detail-accepted-at"
              />
            )}
            {b.started_at && (
              <FieldRow
                label="Started"
                value={_formatIsoLocal(b.started_at)}
                testid="admin-booking-detail-started-at"
              />
            )}
            {b.completed_at && (
              <FieldRow
                label="Completed"
                value={_formatIsoLocal(b.completed_at)}
                testid="admin-booking-detail-completed-at"
              />
            )}
            {b.cancelled_at && (
              <FieldRow
                label="Cancelled"
                value={_formatIsoLocal(b.cancelled_at)}
                testid="admin-booking-detail-cancelled-at"
              />
            )}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          {/* G1 — refund action. Only rendered when there's money to
              refund. Disabled + tooltip wrapped when impersonating
              (backend ALSO returns 403 — defense in depth). */}
          {isPaid && !isAlreadyRefunded ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      onClick={() => onRefund?.(b)}
                      disabled={isImpersonating}
                      aria-disabled={isImpersonating}
                      data-testid="admin-booking-refund-btn"
                      className="text-pp-text"
                    >
                      Refund booking
                    </Button>
                  </span>
                </TooltipTrigger>
                {isImpersonating && (
                  <TooltipContent
                    data-testid="admin-booking-refund-btn-tooltip"
                    className="max-w-xs"
                  >
                    {REFUND_DISABLED_WHILE_IMPERSONATING}
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span />
          )}
          <DialogClose asChild>
            <Button
              variant="outline"
              data-testid="admin-booking-detail-close"
              onClick={onClose}
            >
              Close
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
